<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

import logo from '@/assets/logo.svg';
import { signInTarget } from '@/domains/auth/nav';
import { useCspListQuery, useRegionListQuery } from '@/domains/clusters/queries';
import NotificationPopover from '@/domains/notifications/NotificationPopover.vue';
import { useUnreadNotifications } from '@/domains/notifications/queries';
import { useAuthStore } from '@/platform/auth-runtime/store';
import { useAccountInfoQuery } from '@/platform/identity/queries';
import { useUserStore } from '@/platform/identity/store';
import { useSidebar } from '@/platform/layout/composables/useSidebar';
import {
  CLOUD_CHILDREN_ALL,
  STATIC_NAV,
  type NavItem,
  type SubNavItem,
} from '@/platform/layout/nav-config';
import { Path } from '@/platform/routing/targets';
import { DocsLinks } from '@/shared/docs';
import { SourceFromMap, type SourceFromValue } from '@/shared/source';
import { messageAlert } from '@/shared/ui/message-alert';
import { formatDateTime } from '@/shared/utils/format';
import { HelpOutlined, IdCard, Lock, Logout, UserOutlined } from '@celerdata/icons';
import { ArrowRight, Bell, User } from '@element-plus/icons-vue';
import { storeToRefs } from 'pinia';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { isFullCanvas } = useSidebar();
const auth = useAuthStore();
const user = useUserStore();
const { info } = storeToRefs(user);
const { data: accountInfo } = useAccountInfoQuery();
const { data: cspList } = useCspListQuery();
const { data: regionList } = useRegionListQuery();
const { data: unreadNotifications } = useUnreadNotifications();

// Controls the NotificationPopover open state so child item clicks /
// see-more navigation can close it programmatically via @close.
const notificationPopoverOpen = ref(false);

// Red dot on the sidebar bell signals what the popover surfaces — the
// `notifyList` bucket. Bar broadcasts already render in the top flashbar
// inline, so signalling them on the bell too would double-dip.
const hasUnreadNotifications = computed(
  () => (unreadNotifications.value?.notifyList?.notify_msgs?.length ?? 0) > 0,
);

// Bell entry is "active" when its popover is open OR the route is on the
// dedicated /notification page.
// Kept as a computed instead of inlined in :class so the template stays
// readable AND the read-site is identical to other Sidebar active checks.
const isNotificationActive = computed(
  () => notificationPopoverOpen.value || route.path === Path.Notification,
);

// `/cloud` menu shape — mirrors legacy Menu/index.vue `navItemRoutes` exactly:
//   1. zero available CSPs (regionList ∩ cspList) → hide the /cloud entry
//   2. marketplace account → children = manifest filtered by the sourceFrom
//      tag ONLY. Legacy does not intersect this branch with availability: an
//      unmapped sourceFrom lowercases to '' and lets every CSP through, and a
//      non-CSP tag (Unknown / Gui) matches nothing, leaving the entry visible
//      as an empty, inert submenu.
//   3. otherwise → children = manifest ∩ available
const cloudChildren = computed<SubNavItem[] | null>(() => {
  const csps = cspList.value ?? [];
  const regions = regionList.value ?? [];
  const regionCspIds = new Set(regions.map((r) => r.cspId).filter(Boolean));
  const availablePaths = new Set(
    csps.filter((c) => c.bizId && regionCspIds.has(c.bizId)).map((c) => `/cloud/${c.csp}`),
  );
  if (!availablePaths.size) return null;
  const i = info.value;
  if (i?.isMarketPlace) {
    const tag = (SourceFromMap[i.sourceFrom as SourceFromValue] ?? '').toLowerCase();
    return CLOUD_CHILDREN_ALL.filter((c) => c.path.includes(tag));
  }
  return CLOUD_CHILDREN_ALL.filter((c) => availablePaths.has(c.path));
});

const navItems = computed<NavItem[]>(() => {
  const cloud = cloudChildren.value;
  return STATIC_NAV.filter((item) => !item.hidden).flatMap((item) => {
    if (item.path !== '/cloud') {
      // Drop hidden children too — `hidden` is a manifest field, not a
      // runtime gate.
      const children = item.children?.filter((c) => !c.hidden);
      return [{ ...item, children }];
    }
    // No available CSP → drop the /cloud entry entirely.
    if (cloud === null) return [];
    return [{ ...item, children: cloud }];
  });
});

// Active index matches the most-specific path so ElSubMenu auto-expands
// when on a nested route (e.g. /cloud/aws activates both the /cloud submenu
// and the aws leaf).
const activePath = computed(() => {
  for (const item of navItems.value) {
    if (item.children) {
      const match = item.children.find((c) => route.path.startsWith(c.path));
      if (match) return match.path;
    }
    if (route.path === item.path || route.path.startsWith(`${item.path}/`)) return item.path;
  }
  return '';
});

function onSelect(path: string): void {
  // Compare against fullPath: re-selecting the current menu item while the
  // URL carries query state (/cloud/aws?tab=credential&id=...) pushes the
  // bare path and resets the page to its default view, matching legacy's
  // unconditional push. Only a true no-op (bare path already) is skipped.
  if (path !== route.fullPath) void router.push(path);
}

// Expanding a parent submenu auto-navigates to its first child instead of
// leaving the user on the parent
// path (which usually has no own page and would 404 / show a blank shell).
// Guard: if the user is already anywhere under this parent, don't kick them
// to the first child. This covers two cases element-plus's @open can hit:
//   (a) deep-link to /cloud/gcp → :default-active auto-expands /cloud →
//       @open might fire → without this guard, user lands on /cloud/aws.
//   (b) user manually collapses + re-expands /cloud while on /cloud/gcp →
//       same regression.
function onOpen(parentPath: string): void {
  if (route.path === parentPath || route.path.startsWith(`${parentPath}/`)) return;
  const first = navItems.value.find((i) => i.path === parentPath)?.children?.[0];
  if (first) void router.push(first.path);
}

const displayName = computed(() => {
  const i = info.value;
  if (!i) return 'Account';
  const full = `${i.firstName ?? ''} ${i.lastName ?? ''}`.trim();
  return full || i.email || 'Account';
});

async function onLogout(): Promise<void> {
  try {
    await messageAlert.confirm({ title: t('layout.modal.title') });
  } catch {
    return;
  }
  // Hard reload, not router.push — wujie sub is `alive`, so an SPA logout
  // leaves the previous user's sub instance cached. Clear only persisted
  // auth before navigation; do NOT blank Pinia/query state in this dying
  // document, or the old page can flash empty user/account data before
  // the browser commits the replace.
  auth.clearPersistedAuth();
  window.location.replace(router.resolve(signInTarget()).fullPath);
}
</script>

<template>
  <aside
    v-if="!isFullCanvas"
    class="bg-surface-soft border-line flex w-60 shrink-0 flex-col justify-between border-r py-6 pl-4"
    data-testid="app-sidebar"
  >
    <div class="flex min-h-0 flex-1 flex-col">
      <RouterLink :to="Path.Clusters" class="mt-1 mr-2.5 mb-5 ml-2.5 flex items-center">
        <img :src="logo" alt="PhoenixAI" class="block aspect-180/17 w-45" />
      </RouterLink>

      <ElScrollbar class="min-h-0 flex-1">
        <ElMenu
          :default-active="activePath"
          class="sidebar-menu"
          background-color="transparent"
          @select="onSelect"
          @open="onOpen"
        >
          <template v-for="item in navItems" :key="item.path">
            <div v-if="item.divider" class="border-line mx-2.5 my-1.5 border-t" />
            <!-- Non-null children (even zero-length, e.g. a marketplace tag
                 that matches no CSP) renders a submenu, matching legacy's
                 `v-if="menu.subMenu"`: an empty inert submenu, never a
                 clickable /cloud leaf. -->
            <ElSubMenu v-if="item.children" :index="item.path">
              <template #title>
                <ElIcon :size="22"><component :is="item.icon" /></ElIcon>
                <span>{{ t(`layout.menu.${item.langKey}`) }}</span>
              </template>
              <ElMenuItem v-for="child in item.children" :key="child.path" :index="child.path">
                {{ t(`layout.menu.${child.langKey}`) }}
              </ElMenuItem>
            </ElSubMenu>
            <ElMenuItem v-else :index="item.path">
              <ElIcon :size="22"><component :is="item.icon" /></ElIcon>
              <template #title>{{ t(`layout.menu.${item.langKey}`) }}</template>
            </ElMenuItem>
          </template>
        </ElMenu>
      </ElScrollbar>
    </div>

    <div class="text-fg-2 mr-4 pt-4 text-sm font-semibold">
      <ElPopover
        v-model:visible="notificationPopoverOpen"
        placement="right-end"
        trigger="click"
        :width="324"
        :show-arrow="false"
      >
        <template #reference>
          <button
            type="button"
            class="flex w-full cursor-pointer items-center gap-3.5 rounded p-2.5 text-left focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
            :class="
              isNotificationActive
                ? 'bg-surface-strong text-brand-azure'
                : 'text-fg-2 hover:bg-surface-strong'
            "
            data-testid="sidebar-notifications"
          >
            <span class="relative inline-flex">
              <ElIcon :size="22"><Bell /></ElIcon>
              <span
                v-if="hasUnreadNotifications"
                class="bg-danger ring-surface absolute top-0.5 right-0.5 h-2 w-2 rounded-full ring-2"
                data-testid="sidebar-notifications-dot"
                aria-hidden="true"
              />
            </span>
            <span>{{ t('notification.title') }}</span>
          </button>
        </template>
        <NotificationPopover @close="notificationPopoverOpen = false" />
      </ElPopover>

      <a
        :href="DocsLinks.SignUpOrSignIn"
        target="_blank"
        rel="noopener noreferrer"
        class="text-fg-2 hover:bg-surface-strong flex cursor-pointer items-center gap-3.5 rounded p-2.5 focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-current"
        data-testid="sidebar-docs-link"
      >
        <ElIcon :size="22"><HelpOutlined /></ElIcon>
        <span>{{ t('layout.docs') }}</span>
      </a>
    </div>

    <div class="user-dropdown text-fg-2 mr-4 text-sm font-semibold" data-testid="user-box">
      <ElDropdown trigger="click" placement="top-end">
        <div
          class="text-fg-2 hover:bg-surface-strong flex cursor-pointer items-center gap-3.5 rounded p-2.5"
        >
          <ElIcon :size="22"><User /></ElIcon>
          <span class="truncate">{{ displayName }}</span>
        </div>
        <template #dropdown>
          <ElDropdownMenu class="w-45">
            <ElDropdownItem class="p-0">
              <ElPopover
                placement="right"
                trigger="hover"
                :width="260"
                :offset="4"
                :show-arrow="false"
              >
                <template #reference>
                  <div
                    class="flex w-full items-center justify-between gap-2 px-4 py-1.5"
                    data-testid="sidebar-account-popover"
                  >
                    <span class="flex items-center gap-1.5">
                      <ElIcon :size="14"><UserOutlined /></ElIcon>
                      {{ t('common.account') }}
                    </span>
                    <ElIcon :size="12"><ArrowRight /></ElIcon>
                  </div>
                </template>
                <ElDescriptions :column="1" direction="vertical">
                  <ElDescriptionsItem :label="t('common.accountName')">
                    {{ accountInfo?.accountName || '-' }}
                  </ElDescriptionsItem>
                  <ElDescriptionsItem :label="t('common.accountID')">
                    <span>{{ accountInfo?.bizID || '-' }}</span>
                  </ElDescriptionsItem>
                </ElDescriptions>
              </ElPopover>
            </ElDropdownItem>

            <ElDropdownItem class="p-0">
              <ElPopover
                placement="right"
                trigger="hover"
                :width="260"
                :offset="4"
                :show-arrow="false"
              >
                <template #reference>
                  <div
                    class="flex w-full items-center justify-between gap-2 px-4 py-1.5"
                    data-testid="sidebar-profile-popover"
                  >
                    <span class="flex items-center gap-1.5">
                      <ElIcon :size="14"><IdCard /></ElIcon>
                      {{ t('profile.title') }}
                    </span>
                    <ElIcon :size="12"><ArrowRight /></ElIcon>
                  </div>
                </template>
                <ElDescriptions :column="1" direction="vertical">
                  <ElDescriptionsItem :label="t('common.name')">
                    {{ displayName }}
                  </ElDescriptionsItem>
                  <ElDescriptionsItem :label="t('common.email')">
                    {{ info?.email || '-' }}
                  </ElDescriptionsItem>
                  <ElDescriptionsItem :label="t('profile.loginTime')">
                    {{ formatDateTime(info?.lastLoginAt) }}
                  </ElDescriptionsItem>
                </ElDescriptions>
              </ElPopover>
            </ElDropdownItem>
            <ElDropdownItem
              class="p-0"
              data-testid="security-menu-item"
              @click="() => router.push('/security')"
            >
              <div class="flex w-full items-center gap-1.5 px-4 py-1.5">
                <ElIcon :size="14"><Lock /></ElIcon>
                {{ t('mfa.nav') }}
              </div>
            </ElDropdownItem>

            <ElDropdownItem class="p-0" data-testid="sign-out-menu-item" @click="onLogout">
              <div class="flex w-full items-center gap-1.5 px-4 py-1.5">
                <ElIcon :size="14"><Logout /></ElIcon>
                {{ t('layout.logout') }}
              </div>
            </ElDropdownItem>
          </ElDropdownMenu>
        </template>
      </ElDropdown>
    </div>
  </aside>
</template>

<style scoped>
.sidebar-menu {
  border-right: none;
}
.sidebar-menu :deep(.el-menu-item),
.sidebar-menu :deep(.el-sub-menu__title) {
  height: 40px;
  padding-left: 10px !important;
  padding-right: 10px !important;
  margin-right: 16px;
  border-radius: 4px;
  font-weight: 600;
}
.sidebar-menu :deep(.el-menu-item .el-icon),
.sidebar-menu :deep(.el-sub-menu__title .el-icon) {
  margin-right: 10px;
}
.sidebar-menu :deep(.el-menu-item.is-active),
.sidebar-menu :deep(.el-menu-item:hover),
.sidebar-menu :deep(.el-menu-item:focus),
.sidebar-menu :deep(.el-sub-menu__title:hover) {
  background-color: var(--color-surface-strong);
}
.sidebar-menu :deep(.el-sub-menu .el-menu-item) {
  padding-left: 42px !important;
}
/* Element Plus defaults to right: 20px on the chevron, which combined
   with our 10px title padding leaves an off-center gap. Legacy used
   right: 10px (Menu/index.vue old scss). */
.sidebar-menu :deep(.el-sub-menu__title .el-sub-menu__icon-arrow) {
  right: 10px;
}

/* ElDropdown's root <div class="el-dropdown"> is inline-block by default,
   so the trigger pill only sized to its content. Force it to span the
   sidebar column so the hover target matches the menu items above. */
.user-dropdown :deep(.el-dropdown) {
  display: block;
  width: 100%;
}
</style>
